import { useEffect, useRef, useState } from "react";
import { SyncBar } from "@/components/SyncBar";
import { DateRange } from "@/components/DateRange";
import { Link } from "react-router-dom";
import { Panel } from "@/components/Panel";
import { Tile } from "@/components/Tile";
import { ProgressRow } from "@/components/viz/ProgressRow";

export default function GeoPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [byProv, setByProv] = useState<any[]>([]);
  const [unmapped, setUnmapped] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>({});
  const [svg, setSvg] = useState<string>("");
  const [hover, setHover] = useState<{ name: string; count: number; x: number; y: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const today = new Date().toISOString().slice(0, 10);
  const defStart = new Date(Date.now() + 7 * 3600e3 - 86400e3).toISOString().slice(0, 10); // default: Kemarin (H-1 WIB)
  const [start, setStart] = useState(defStart);
  const [end, setEnd] = useState(today);

  useEffect(() => { fetch("/map_indonesia.svg").then((r) => r.text()).then(setSvg); }, []);
  async function load(sOv?: string, eOv?: string) {
    const r = await fetch(`/api/v1/metrics/geo?start=${sOv ?? start}&end=${eOv ?? end}`);
    if (r.status === 401) { location.href = "/login"; return; }
    const j = await r.json();
    setRows(j.data ?? []); setByProv(j.byProvince ?? []); setUnmapped(j.unmapped ?? []); setMeta(j.meta ?? {});
  }
  useEffect(() => { load(); }, []);

  // Warnai + pasang interaksi setiap kali svg atau data berubah
  useEffect(() => {
    const el = wrapRef.current; if (!el || !svg) return;
    const max = byProv[0]?.count ?? 0;
    const countOf = new Map(byProv.map((p: any) => [p.code, p.count]));
    const nameOf = new Map(byProv.map((p: any) => [p.code, p.name]));
    el.querySelectorAll<SVGPathElement>("g#features path[id^='ID']").forEach((path) => {
      const code = path.id;
      const c = countOf.get(code) ?? 0;
      const t = max ? c / max : 0;
      // skala biru navy: 0 => abu muda
      path.style.fill = c === 0 ? "#EEF1F6" : `rgba(42,120,214,${0.22 + t * 0.78})`;
      path.style.cursor = c > 0 ? "pointer" : "default";
      path.style.transition = "fill .15s";
      path.onmousemove = (ev: MouseEvent) => {
        const r = el.getBoundingClientRect();
        setHover({ name: (nameOf.get(code) ?? path.getAttribute("name") ?? code) as string, count: c, x: ev.clientX - r.left + 12, y: ev.clientY - r.top + 12 });
      };
      path.onmouseleave = () => setHover(null);
      path.onclick = () => {
        if (c === 0) return;
        const label = (nameOf.get(code) ?? path.getAttribute("name") ?? code) as string;
        location.href = `/a/monitoring/articles?${new URLSearchParams({ province: code, provinceLabel: label, start, end } as any)}`;
      };
    });
  }, [svg, byProv, start, end]);

  const maxCity = rows[0]?.count ?? 1;
  const provSum = byProv.reduce((a: number, p: any) => a + p.count, 0);
  const citySum = rows.reduce((a: number, r: any) => a + r.count, 0);
  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Sebaran Lokasi</h1>
          <div className="avio-t-sm avio-muted">Media massa · {start} → {end} (WIB) · klik provinsi untuk melihat artikelnya</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); load(s2, e2); }} />
        </div>
      </div>
      <SyncBar wing="media" />

      <div className="avio-tiles">
        <Tile c={1} icon="fa-solid fa-map-location-dot" label="Artikel berlokasi" value={meta.located ?? 0}
          sub={`${meta.coverage ?? 0}% dari ${(meta.total ?? 0).toLocaleString("id-ID")} artikel`} />
        <Tile c={3} icon="fa-solid fa-map" label="Provinsi terjangkau" value={byProv.length} sub="dari 38 provinsi" />
        <Tile c={5} icon="fa-solid fa-location-dot" label="Kota / kabupaten" value={rows.length} sub="titik unik" />
      </div>

      <div className="avio-grid">
        <div data-col="7">
          <Panel icon="fa-solid fa-map-location-dot" title="Peta sebaran" hint="warna = intensitas artikel">
            <div ref={wrapRef} style={{ position: "relative" }}>
              <div dangerouslySetInnerHTML={{ __html: svg }} />
              {hover && (
                <div style={{ position: "absolute", left: hover.x, top: hover.y, background: "var(--gray-900, #10141C)", color: "#fff", padding: "var(--space-4) var(--space-5)", borderRadius: "var(--radius-sm)", fontSize: "var(--text-sm)", pointerEvents: "none", whiteSpace: "nowrap", zIndex: 10 }}>
                  <b>{hover.name}</b> · {hover.count.toLocaleString("id-ID")} artikel
                </div>
              )}
            </div>
          </Panel>
        </div>
        <div data-col="5">
          <Panel icon="fa-solid fa-ranking-star" title="Provinsi teratas">
            {byProv.length === 0
              ? <div className="avio-empty"><i className="fa-regular fa-map avio-empty__icon" /><div className="avio-empty__title">Belum ada data lokasi.</div><div className="avio-empty__hint">Jalankan Sync Lokasi lebih dulu.</div></div>
              : <div className="avio-plist">
                  {byProv.slice(0, 10).map((p2: any) => (
                    <ProgressRow key={p2.code} name={p2.name} total={p2.count}
                      share={provSum ? (p2.count / provSum) * 100 : 0}
                      maxShare={provSum ? ((byProv[0]?.count ?? 0) / provSum) * 100 : 0}
                      href={`/a/monitoring/articles?province=${p2.code}&provinceLabel=${encodeURIComponent(p2.name)}&start=${start}&end=${end}`}
                      segs={[{ value: p2.count, color: "var(--mark-c1)", title: `${p2.name}: ${p2.count}` }]} />
                  ))}
                </div>}
          </Panel>
        </div>
        <div data-col="7">
          <Panel icon="fa-solid fa-location-dot" title="Kota / kabupaten" hint="top 50">
            <div className="avio-plist" style={{ maxHeight: 460, overflowY: "auto" }}>
              {rows.map((r: any) => (
                <ProgressRow key={r.location} name={r.location} total={r.count}
                  share={citySum ? (r.count / citySum) * 100 : 0}
                  maxShare={citySum ? (maxCity / citySum) * 100 : 0}
                  href={`/a/monitoring/articles?location=${encodeURIComponent(r.location)}&start=${start}&end=${end}`}
                  segs={[{ value: r.count, color: "var(--mark-c3)", title: `${r.location}: ${r.count}` }]} />
              ))}
              {rows.length === 0 && <div className="avio-empty"><i className="fa-regular fa-map avio-empty__icon" /><div className="avio-empty__title">Belum ada kota terdeteksi.</div></div>}
            </div>
          </Panel>
        </div>
        {unmapped.length > 0 && (
          <div data-col="5">
            <Panel icon="fa-solid fa-triangle-exclamation" title="Belum terpetakan" hint={`${unmapped.length} lokasi`}>
              <div className="avio-t-sm avio-muted" style={{ marginBottom: "var(--space-5)" }}>
                Nama lokasi ini belum dikenali pemetaan provinsi — biasanya ejaan vendor berbeda. Tidak dibuang, hanya belum masuk peta.
              </div>
              <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap" }}>
                {unmapped.map((x: any) => (
                  <span key={x.location} className="avio-chip" data-tone="soft"><span className="avio-chip__label">{x.location} · {x.count}</span></span>
                ))}
              </div>
            </Panel>
          </div>
        )}
      </div>
      {byProv.length > 0 && <div className="avio-t-sm avio-muted">Klik provinsi pada peta atau baris daftar untuk membuka daftar artikelnya. <Link to="/a/sync">Kelola sync lokasi →</Link></div>}
    </div>
  );
}
